import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d69rhc5bd17lru29lel0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Thank you! We will contact you soon.');
        form.reset();
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      alert('Failed to submit. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/420e03feb318075440c27939f2e5b069.png" 
              alt="Sophia Systems AI" 
              className="h-12 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-8">
            <a href="#services" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900 hover:text-[#C8A75E]' : 'text-white hover:text-[#C8A75E]'}`}>
              Services
            </a>
            <a href="#process" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900 hover:text-[#C8A75E]' : 'text-white hover:text-[#C8A75E]'}`}>
              Process
            </a>
            <a href="#results" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-900 hover:text-[#C8A75E]' : 'text-white hover:text-[#C8A75E]'}`}>
              Results
            </a>
            <a 
              href="#contact" 
              className="px-6 py-2.5 bg-[#C8A75E] text-[#1C1C1E] text-sm font-semibold rounded-md hover:bg-[#B89650] transition-all whitespace-nowrap cursor-pointer"
            >
              Get Started
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/37001fbf942aa35013918e5e4a5d59a4.jpg"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-8 text-center py-32 w-full">
          <h1 className="font-serif text-7xl text-white mb-4 tracking-wide animate-fade-in">
            Intelligent Systems. Beautifully Built.
          </h1>
          <div className="w-64 h-px bg-[#C8A75E] mx-auto mb-12"></div>
          <p className="text-2xl text-gray-200 mb-16 max-w-2xl mx-auto leading-relaxed">
            SophiaSystems AI helps home service businesses modernize operations, automate customer engagement, and scale revenue with refined, intelligent systems.
          </p>
          <div className="flex items-center justify-center gap-6">
            <a 
              href="#contact" 
              className="px-8 py-4 bg-[#C8A75E] text-[#1C1C1E] text-base font-semibold rounded-lg hover:bg-[#B89650] transition-all whitespace-nowrap cursor-pointer"
            >
              Schedule a Strategy Call
            </a>
            <a 
              href="#services" 
              className="px-8 py-4 border border-[#C8A75E] text-[#C8A75E] text-base font-semibold rounded-lg hover:bg-[#C8A75E] hover:text-[#1C1C1E] transition-all whitespace-nowrap cursor-pointer"
            >
              Explore Our Systems
            </a>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="bg-[#F8F5F1] py-32">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-5xl text-[#1F2A44] mb-8">
                Home Service Businesses Deserve Better Systems
              </h2>
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                Most home service companies rely on disconnected tools, manual follow-ups, missed calls, and inconsistent lead management. Growth becomes chaotic instead of controlled.
              </p>
              <p className="text-xl text-gray-700 mb-10 leading-relaxed font-medium">
                SophiaSystems AI replaces operational guesswork with structured intelligence.
              </p>
              <ul className="space-y-5">
                <li className="flex items-start gap-4">
                  <span className="text-[#C8A75E] text-2xl font-light">—</span>
                  <span className="text-lg text-gray-800">Missed calls = missed revenue</span>
                </li>
                <li className="flex items-start gap-4">
                  <span className="text-[#C8A75E] text-2xl font-light">—</span>
                  <span className="text-lg text-gray-800">Manual scheduling inefficiencies</span>
                </li>
                <li className="flex items-start gap-4">
                  <span className="text-[#C8A75E] text-2xl font-light">—</span>
                  <span className="text-lg text-gray-800">Poor lead follow-up</span>
                </li>
                <li className="flex items-start gap-4">
                  <span className="text-[#C8A75E] text-2xl font-light">—</span>
                  <span className="text-lg text-gray-800">Limited visibility into pipeline performance</span>
                </li>
              </ul>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=modern%20professional%20business%20operations%20dashboard%20with%20clean%20interface%20showing%20analytics%20and%20workflow%20automation%20minimal%20elegant%20design%20soft%20lighting%20ivory%20and%20navy%20color%20scheme%20sophisticated%20technology%20interface%20high%20quality%20professional%20photography&width=600&height=700&seq=problem-section-img&orientation=portrait" 
                alt="Business Operations" 
                className="w-full h-[700px] object-cover object-top rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Solution Framework Section */}
      <section className="bg-[#F8F5F1] py-32">
        <div className="max-w-7xl mx-auto px-8">
          <h2 className="font-serif text-5xl text-[#1F2A44] text-center mb-20">
            The Sophia Systems Framework
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Systemization',
                description: 'We create structured workflows that remove operational chaos.',
                image: 'https://readdy.ai/api/search-image?query=abstract%20geometric%20pattern%20representing%20organized%20workflow%20systems%20clean%20minimal%20design%20with%20gold%20and%20charcoal%20accents%20interconnected%20nodes%20and%20lines%20elegant%20business%20concept%20illustration&width=400&height=300&seq=framework-1&orientation=landscape'
              },
              {
                title: 'Automation',
                description: 'AI-driven booking, follow-up, and pipeline management.',
                image: 'https://readdy.ai/api/search-image?query=futuristic%20automation%20concept%20with%20robotic%20elements%20and%20digital%20interfaces%20sleek%20modern%20design%20gold%20highlights%20on%20dark%20background%20artificial%20intelligence%20visualization%20elegant%20tech%20aesthetic&width=400&height=300&seq=framework-2&orientation=landscape'
              },
              {
                title: 'Customer Experience',
                description: 'Intelligent communication systems that convert more leads.',
                image: 'https://readdy.ai/api/search-image?query=customer%20service%20excellence%20concept%20with%20connected%20communication%20icons%20chat%20bubbles%20and%20happy%20customer%20symbols%20warm%20gold%20tones%20modern%20minimal%20design%20professional%20business%20illustration&width=400&height=300&seq=framework-3&orientation=landscape'
              },
              {
                title: 'Revenue Intelligence',
                description: 'Clear dashboards, performance tracking, and forecasting.',
                image: 'https://readdy.ai/api/search-image?query=business%20analytics%20dashboard%20visualization%20with%20charts%20graphs%20and%20growth%20metrics%20elegant%20gold%20and%20navy%20color%20scheme%20modern%20data%20visualization%20clean%20professional%20design%20financial%20intelligence%20concept&width=400&height=300&seq=framework-4&orientation=landscape'
              }
            ].map((card, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg overflow-hidden border-t-2 border-[#C8A75E] hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer"
              >
                <div className="w-full h-48 overflow-hidden">
                  <img 
                    src={card.image}
                    alt={card.title}
                    className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-[#1F2A44] mb-4">
                    {card.title}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services / Packages Section */}
      <section id="services" className="relative bg-[#1C1C1E] py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20geometric%20pattern%20with%20subtle%20gold%20accents%20luxury%20business%20background%20elegant%20minimal%20design%20interconnected%20lines%20and%20shapes%20sophisticated%20texture&width=1920&height=1200&seq=services-bg&orientation=landscape"
            alt="Services Background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-8">
          <h2 className="font-serif text-5xl text-white text-center mb-20">
            AI Systems for Every Stage of Growth
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Essential Systems */}
            <div className="bg-[#252527] rounded-lg p-16 border border-[#3A3A3A] hover:border-[#C8A75E] transition-all duration-300">
              <h3 className="font-serif text-3xl text-[#C8A75E] text-center mb-8">
                Essential Systems
              </h3>
              <ul className="space-y-4 mb-12">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">AI Booking Assistant</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">Missed Call Text-Back</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">Review Automation</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">CRM Setup</span>
                </li>
              </ul>
              <button className="w-full py-4 border border-[#C8A75E] text-[#C8A75E] text-base font-semibold rounded-lg hover:bg-[#C8A75E] hover:text-[#1C1C1E] transition-all whitespace-nowrap cursor-pointer">
                Start Smart
              </button>
            </div>

            {/* Intelligent Operations - Highlighted */}
            <div className="bg-[#252527] rounded-lg p-16 border-2 border-[#C8A75E] shadow-[0_0_30px_rgba(200,167,94,0.3)] lg:-translate-y-6">
              <h3 className="font-serif text-3xl text-[#C8A75E] text-center mb-8">
                Intelligent Operations
              </h3>
              <ul className="space-y-4 mb-12">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">Lead Scoring AI</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">Automated Estimate Follow-Up</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">Workflow Automation</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">Reputation Engine</span>
                </li>
              </ul>
              <button className="w-full py-4 bg-[#C8A75E] text-[#1C1C1E] text-base font-semibold rounded-lg hover:bg-[#B89650] transition-all whitespace-nowrap cursor-pointer">
                Scale Smarter
              </button>
            </div>

            {/* Enterprise Precision */}
            <div className="bg-[#252527] rounded-lg p-16 border border-[#3A3A3A] hover:border-[#C8A75E] transition-all duration-300">
              <h3 className="font-serif text-3xl text-[#C8A75E] text-center mb-8">
                Enterprise Precision
              </h3>
              <ul className="space-y-4 mb-12">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">Multi-location routing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">Advanced analytics dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">AI pipeline forecasting</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#C8A75E] text-xl mt-1 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-gray-300 text-lg">Custom integrations</span>
                </li>
              </ul>
              <button className="w-full py-4 border border-[#C8A75E] text-[#C8A75E] text-base font-semibold rounded-lg hover:bg-[#C8A75E] hover:text-[#1C1C1E] transition-all whitespace-nowrap cursor-pointer">
                Build at Scale
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="bg-[#F8F5F1] py-32">
        <div className="max-w-7xl mx-auto px-8">
          <h2 className="font-serif text-5xl text-[#1F2A44] text-center mb-20">
            Our Implementation Process
          </h2>
          <div className="relative">
            {/* Connecting Line */}
            <div className="absolute top-10 left-0 right-0 h-0.5 bg-[#C8A75E] hidden lg:block"></div>
            
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 relative">
              {[
                {
                  number: '1',
                  title: 'Audit & Discovery',
                  description: 'We evaluate your current systems and operational bottlenecks.',
                  image: 'https://readdy.ai/api/search-image?query=business%20audit%20and%20discovery%20concept%20magnifying%20glass%20examining%20data%20charts%20professional%20analysis%20elegant%20gold%20and%20navy%20design%20minimal%20clean%20illustration&width=300&height=200&seq=process-1&orientation=landscape'
                },
                {
                  number: '2',
                  title: 'Architecture & Design',
                  description: 'We design intelligent workflows tailored to your business.',
                  image: 'https://readdy.ai/api/search-image?query=system%20architecture%20blueprint%20design%20concept%20with%20flowcharts%20and%20wireframes%20elegant%20professional%20illustration%20gold%20accents%20on%20light%20background%20modern%20business%20planning&width=300&height=200&seq=process-2&orientation=landscape'
                },
                {
                  number: '3',
                  title: 'Deployment',
                  description: 'We implement automation across communication, CRM, and tracking.',
                  image: 'https://readdy.ai/api/search-image?query=software%20deployment%20and%20implementation%20concept%20rocket%20launch%20with%20digital%20elements%20modern%20tech%20illustration%20gold%20and%20charcoal%20color%20scheme%20professional%20business%20aesthetic&width=300&height=200&seq=process-3&orientation=landscape'
                },
                {
                  number: '4',
                  title: 'Optimization',
                  description: 'We refine and improve based on performance data.',
                  image: 'https://readdy.ai/api/search-image?query=performance%20optimization%20concept%20with%20upward%20trending%20graphs%20and%20fine%20tuning%20dials%20elegant%20business%20illustration%20gold%20highlights%20modern%20minimal%20design%20continuous%20improvement&width=300&height=200&seq=process-4&orientation=landscape'
                }
              ].map((step, index) => (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="w-20 h-20 rounded-full bg-[#C8A75E] flex items-center justify-center text-[#1C1C1E] text-2xl font-bold mb-6 relative z-10">
                    {step.number}
                  </div>
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-6">
                    <img 
                      src={step.image}
                      alt={step.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-[#1F2A44] mb-4">
                    {step.title}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed max-w-xs">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Study Section */}
      <section id="results" className="bg-[#F8F5F1] py-32">
        <div className="max-w-7xl mx-auto px-8">
          <h2 className="font-serif text-5xl text-[#1F2A44] text-center mb-20">
            Real Results for Real Service Businesses
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16">
            {[
              {
                badge: 'HVAC Company',
                metric: '38% Increase in Booked Jobs',
                description: 'AI scheduling + automated follow-up reduced missed opportunities.',
                image: 'https://readdy.ai/api/search-image?query=professional%20HVAC%20technician%20working%20on%20modern%20air%20conditioning%20unit%20clean%20professional%20photography%20warm%20lighting%20home%20service%20business%20success%20story&width=400&height=250&seq=case-1&orientation=landscape'
              },
              {
                badge: 'Roofing Contractor',
                metric: '2X Lead Conversion',
                description: 'Lead scoring prioritized high-intent inquiries.',
                image: 'https://readdy.ai/api/search-image?query=professional%20roofing%20contractor%20team%20working%20on%20residential%20roof%20sunny%20day%20professional%20photography%20home%20improvement%20business%20success%20clean%20modern%20aesthetic&width=400&height=250&seq=case-2&orientation=landscape'
              },
              {
                badge: 'Plumbing Group',
                metric: '42% Faster Response Time',
                description: 'Missed call automation captured every opportunity.',
                image: 'https://readdy.ai/api/search-image?query=professional%20plumber%20working%20under%20sink%20modern%20bathroom%20professional%20photography%20home%20service%20business%20clean%20bright%20lighting%20success%20story&width=400&height=250&seq=case-3&orientation=landscape'
              }
            ].map((study, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer"
              >
                <div className="w-full h-48 overflow-hidden">
                  <img 
                    src={study.image}
                    alt={study.badge}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-8">
                  <div className="inline-block px-4 py-2 bg-[#C8A75E] text-[#1C1C1E] text-sm font-semibold rounded-full mb-4">
                    {study.badge}
                  </div>
                  <h3 className="text-2xl font-bold text-[#1F2A44] mb-3">
                    {study.metric}
                  </h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    {study.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <button className="px-8 py-4 border-2 border-[#C8A75E] text-[#C8A75E] text-base font-semibold rounded-lg hover:bg-[#C8A75E] hover:text-[#1C1C1E] transition-all whitespace-nowrap cursor-pointer">
              View Case Studies
            </button>
          </div>
        </div>
      </section>

      {/* Trust / Differentiator Section */}
      <section className="relative bg-[#1C1C1E] py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=elegant%20abstract%20technology%20background%20with%20flowing%20golden%20lines%20and%20particles%20on%20dark%20charcoal%20background%20sophisticated%20luxury%20business%20aesthetic%20futuristic%20minimal%20design&width=1920&height=800&seq=trust-bg&orientation=landscape"
            alt="Trust Background"
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1E] via-transparent to-[#1C1C1E]"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-8 text-center">
          <h2 className="font-serif text-5xl text-white mb-12 leading-tight">
            Not a Marketing Agency.<br />
            An Intelligent Systems Partner.
          </h2>
          <p className="text-2xl text-gray-300 leading-relaxed">
            SophiaSystems AI is built on structure, precision, and long-term operational growth. We don't chase trends. We build sustainable systems.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contact" className="relative bg-[#1C1C1E] py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=modern%20executive%20office%20with%20panoramic%20city%20view%20at%20dusk%20elegant%20interior%20design%20warm%20ambient%20lighting%20sophisticated%20business%20environment%20dark%20charcoal%20and%20gold%20accents%20luxury%20professional%20setting&width=1920&height=1200&seq=cta-bg&orientation=landscape"
            alt="CTA Background"
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1E] via-[#1C1C1E]/80 to-[#1C1C1E]"></div>
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-8 text-center">
          <h2 className="font-serif text-6xl text-white mb-10">
            Ready to Modernize Your Operations?
          </h2>
          <p className="text-2xl text-gray-300 mb-14 max-w-2xl mx-auto">
            Let's build intelligent systems tailored to your service business.
          </p>
          
          {/* Contact Form */}
          <form 
            onSubmit={handleFormSubmit}
            data-readdy-form
            id="strategy-call-form"
            className="max-w-xl mx-auto space-y-6"
          >
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              required
              className="w-full px-6 py-4 bg-[#252527] border border-[#3A3A3A] text-white rounded-lg focus:outline-none focus:border-[#C8A75E] transition-colors text-base"
            />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              required
              className="w-full px-6 py-4 bg-[#252527] border border-[#3A3A3A] text-white rounded-lg focus:outline-none focus:border-[#C8A75E] transition-colors text-base"
            />
            <input
              type="tel"
              name="phone"
              placeholder="Phone Number"
              required
              className="w-full px-6 py-4 bg-[#252527] border border-[#3A3A3A] text-white rounded-lg focus:outline-none focus:border-[#C8A75E] transition-colors text-base"
            />
            <input
              type="text"
              name="company"
              placeholder="Company Name"
              required
              className="w-full px-6 py-4 bg-[#252527] border border-[#3A3A3A] text-white rounded-lg focus:outline-none focus:border-[#C8A75E] transition-colors text-base"
            />
            <textarea
              name="message"
              placeholder="Tell us about your business needs"
              rows={4}
              maxLength={500}
              required
              className="w-full px-6 py-4 bg-[#252527] border border-[#3A3A3A] text-white rounded-lg focus:outline-none focus:border-[#C8A75E] transition-colors resize-none text-base"
            ></textarea>
            <button 
              type="submit"
              className="w-full py-5 bg-[#C8A75E] text-[#1C1C1E] text-lg font-semibold rounded-lg hover:bg-[#B89650] transition-all whitespace-nowrap cursor-pointer animate-pulse-glow"
            >
              Book Your Strategy Call
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F8F5F1] text-[#1F2A44] py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
            {/* Brand Column */}
            <div className="md:col-span-1">
              <img 
                src="https://static.readdy.ai/image/0fc33fa68f46f743570ad8ba1c3172ea/420e03feb318075440c27939f2e5b069.png" 
                alt="Sophia Systems AI" 
                className="h-16 w-auto object-contain mb-6"
              />
              <p className="text-gray-600 text-base mb-8 leading-relaxed">
                Building intelligent systems for home service businesses.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="w-10 h-10 border border-[#C8A75E] rounded-full flex items-center justify-center hover:bg-[#C8A75E] hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-[#C8A75E] rounded-full flex items-center justify-center hover:bg-[#C8A75E] hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                </a>
                <a href="#" className="w-10 h-10 border border-[#C8A75E] rounded-full flex items-center justify-center hover:bg-[#C8A75E] hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>

            {/* Services Column */}
            <div>
              <h4 className="text-sm uppercase tracking-wider mb-6 font-semibold">Services</h4>
              <ul className="space-y-3">
                <li><a href="#services" className="text-gray-600 hover:text-[#C8A75E] transition-colors cursor-pointer">Essential Systems</a></li>
                <li><a href="#services" className="text-gray-600 hover:text-[#C8A75E] transition-colors cursor-pointer">Intelligent Operations</a></li>
                <li><a href="#services" className="text-gray-600 hover:text-[#C8A75E] transition-colors cursor-pointer">Enterprise Precision</a></li>
              </ul>
            </div>

            {/* Company Column */}
            <div>
              <h4 className="text-sm uppercase tracking-wider mb-6 font-semibold">Company</h4>
              <ul className="space-y-3">
                <li><a href="#process" className="text-gray-600 hover:text-[#C8A75E] transition-colors cursor-pointer">Process</a></li>
                <li><a href="#results" className="text-gray-600 hover:text-[#C8A75E] transition-colors cursor-pointer">Case Studies</a></li>
                <li><a href="#contact" className="text-gray-600 hover:text-[#C8A75E] transition-colors cursor-pointer">Contact</a></li>
              </ul>
            </div>

            {/* Contact Column */}
            <div>
              <h4 className="text-sm uppercase tracking-wider mb-6 font-semibold">Contact</h4>
              <ul className="space-y-3">
                <li className="text-gray-600">contact@sophiasystems.ai</li>
                <li className="text-gray-600">1-800-SOPHIA-AI</li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-8 border-t border-[#C8A75E]">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-600">
                © 2025 SophiaSystems AI. All rights reserved.
              </p>
              <div className="flex items-center gap-8">
                <a href="#" className="text-sm text-gray-600 hover:text-[#C8A75E] transition-colors cursor-pointer">Privacy Policy</a>
                <a href="#" className="text-sm text-gray-600 hover:text-[#C8A75E] transition-colors cursor-pointer">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
